import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Alert,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper, 
  IconButton,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon,
  CurrencyRupee as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import useCustomToast from '../hooks/useCustomToast';
import axios from 'axios';
import RAZORPAY_CONFIG, { validateRazorpayConfig } from '../config/razorpay';

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(window.Razorpay);
    document.body.appendChild(script);
  });
};

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  },
}));

const InvoiceCard = styled(Card)(({ theme, status }) => ({
  borderRadius: 12,
  border: `2px solid ${
    status === 'Paid' ? '#4CAF50' : 
    status === 'Unpaid' ? '#FF9800' : 
    status === 'Overdue' ? '#F44336' : '#E0E0E0'
  }`,
  background: status === 'Paid' ? 'rgba(76, 175, 80, 0.05)' : 
             status === 'Unpaid' ? 'rgba(255, 152, 0, 0.05)' :
             status === 'Overdue' ? 'rgba(244, 67, 54, 0.05)' : '#f9f9f9',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  },
}));

const PayInvoiceDetails = ({ invoiceData, onBack, onViewReservations, hotelId, hotelName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { successToast, errorToast } = useCustomToast();
  
  // State for checkboxes
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [reservationsDialog, setReservationsDialog] = useState({ open: false, month: '', reservations: [], loading: false });

  // Initialize all invoices as selected by default
  useEffect(() => {
    if (invoiceData && Array.isArray(invoiceData)) {
      const allUnpaidIds = new Set(
        invoiceData
          .filter(invoice => invoice.status?.toLowerCase() !== 'paid')
          .map(invoice => invoice.id)
      );
      setSelectedInvoices(allUnpaidIds);
    }
  }, [invoiceData]);

  const formatCurrency = (amount) => {
    const value = parseFloat(amount) || 0;
    return `₹${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'unpaid':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return <CheckCircleIcon />;
      case 'unpaid':
        return <WarningIcon />;
      case 'overdue':
        return <CancelIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const handleDownloadPDF = (pdfUrl, invoiceType, invoiceId) => {
    if (!pdfUrl) {
      errorToast('PDF file not available for this invoice');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${invoiceType}_${invoiceId}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      successToast('PDF download started');
    } catch (error) {
      errorToast('Failed to download PDF');
    }
  };

  const calculateSelectedTotal = () => {
    if (!invoiceData || !Array.isArray(invoiceData)) return 0;
    
    return invoiceData.reduce((total, invoice) => {
      if (selectedInvoices.has(invoice.id) && invoice.status?.toLowerCase() !== 'paid') {
        return total + (parseFloat(invoice.total_amount) || 0);
      }
      return total;
    }, 0);
  };

  const calculateGrandTotal = () => {
    if (!invoiceData || !Array.isArray(invoiceData)) return 0;
    
    return invoiceData.reduce((total, invoice) => {
      if (invoice.status?.toLowerCase() !== 'paid') {
        return total + (parseFloat(invoice.total_amount) || 0);
      }
      return total;
    }, 0);
  };

  const handleToggleInvoice = (invoiceId, isPaid) => {
    if (isPaid) return;
    
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  const handleToggleAll = () => {
    const allUnpaidIds = invoiceData
      .filter(invoice => invoice.status?.toLowerCase() !== 'paid')
      .map(invoice => invoice.id);
    
    if (selectedInvoices.size === allUnpaidIds.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(allUnpaidIds));
    }
  };

  const handlePaySelected = async () => {
    if (selectedInvoices.size === 0) {
      errorToast('Please select at least one invoice to pay');
      return;
    }

    try {
      if (!validateRazorpayConfig()) {
        errorToast('Payment system configuration incomplete');
        return;
      }

      if (!hotelId) {
        errorToast('Hotel ID not found');
        return;
      }

      const selectedInvoiceIds = Array.from(selectedInvoices);
      
      const generateOrderPayload = {
        hotel_id: hotelId,
        selected_invoice: selectedInvoiceIds,
        currency: 'INR'
      };

      const generateOrderResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/generateInvoiceOrder',
        generateOrderPayload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (!generateOrderResponse.data.status) {
        throw new Error(generateOrderResponse.data.message || 'Failed to generate invoice order');
      }

      const orderData = generateOrderResponse.data.order;
      const invoiceIdsFromResponse = generateOrderResponse.data.invoice_ids;

      const Razorpay = await loadRazorpayScript();
      
      const options = {
        key: RAZORPAY_CONFIG.KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: 'TabTrips',
        description: 'Invoice Payment',
        order_id: orderData.id,
        handler: async function (response) {
          try {
            const submitPaymentPayload = {
              hotel_id: hotelId,
              invoice_ids: invoiceIdsFromResponse,
              order_id: orderData.id,
              razorpay_signature: response.razorpay_signature,
              payment_id: response.razorpay_payment_id
            };

            const submitPaymentResponse = await axios.post(
              'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/submitPaymentRecord',
              submitPaymentPayload,
              { headers: { 'Content-Type': 'application/json' } }
            );

            if (submitPaymentResponse.data.status) {
              successToast('Payment successful! Selected invoices have been marked as paid.');
              window.location.reload();
            } else {
              errorToast(submitPaymentResponse.data.message || 'Failed to update payment record');
            }
          } catch (error) {
            errorToast('Payment successful but failed to update records. Please contact support.');
          }
        },
        prefill: {
          name: hotelName || 'Hotel',
          email: 'hotel@example.com',
          contact: ''
        },
        theme: {
          color: '#4CAF50'
        },
        modal: {
          ondismiss: function() {
            // Payment modal dismissed
          }
        }
      };

      const rzp = new Razorpay(options);
      rzp.open();
      
    } catch (error) {
      errorToast(error.message || 'Failed to process payment');
    }
  };

  const fetchReservations = async (monthKey, formattedMonth) => {
    if (!hotelId) {
      errorToast('Hotel ID not found');
      return;
    }

    setReservationsDialog(prev => ({ ...prev, loading: true, open: true, month: monthKey }));
    
    try {
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getReservation',
        {
          hotel_id: hotelId,
          month: formattedMonth
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.status) {
        setReservationsDialog({
          open: true,
          month: monthKey,
          reservations: response.data.data || [],
          loading: false
        });
      } else {
        errorToast(response.data.message || 'No reservations found for this month');
        setReservationsDialog({
          open: true,
          month: monthKey,
          reservations: [],
          loading: false
        });
      }
    } catch (err) {
      console.error('Error fetching reservations:', err);
      errorToast('Failed to fetch reservation details');
      setReservationsDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleViewReservations = (monthKey) => {
    const formattedMonth = dayjs(monthKey).format('MM/YYYY');
    fetchReservations(monthKey, formattedMonth);
  };

  const groupInvoicesByMonth = () => {
    if (!invoiceData || !Array.isArray(invoiceData)) return {};
    
    const groups = invoiceData.reduce((groups, invoice) => {
      const month = invoice.month;
      if (!groups[month]) {
        groups[month] = [];
      }
      groups[month].push(invoice);
      return groups;
    }, {});
    
    // Sort months in descending order (latest first)
    const sortedGroups = {};
    Object.keys(groups)
      .sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())
      .forEach(month => {
        sortedGroups[month] = groups[month];
      });
    
    return sortedGroups;
  };

  const groupedInvoices = groupInvoicesByMonth();
  const selectedTotal = calculateSelectedTotal();
  const grandTotal = calculateGrandTotal();

  if (!invoiceData || !Array.isArray(invoiceData)) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          No invoice data available. Please generate invoice first.
        </Alert>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ borderRadius: 3 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box 
      className="invoice-container"
      sx={{ 
        p: { xs: 2, md: 4 },
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF7700 0%, #FFA040 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(255, 119, 0, 0.3)'
          }}>
            <ReceiptIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
          </Box>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: '#1e293b' }}>
              Invoice Payment - {hotelName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoiceData.length} invoices • {selectedInvoices.size} selected
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards - Reordered */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Invoice Card - First */}
        {(() => {
          const unpaidCount = invoiceData.filter(i => i.status?.toLowerCase() !== 'paid').length;
          const isAllPaid = unpaidCount === 0;
          
          return (
            <Grid item xs={12} md={6}>
              <StyledCard sx={{ 
                background: isAllPaid 
                  ? 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)'
                  : 'linear-gradient(135deg, #FF7700 0%, #FFA040 100%)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                height: '100%',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <ReceiptIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />
                    <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600} color="white">
                      Invoice Status
                    </Typography>
                  </Box>
                  
                  {/* Main Content */}
                  <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative'
                  }}>
                    {isAllPaid ? (
                      <Typography 
                        variant="h6"
                        sx={{ 
                          color: 'white', 
                          fontWeight: 600,
                          textAlign: 'center',
                          fontSize: { xs: '1.1rem', sm: '1.25rem' }
                        }}
                      >
                        Paid
                      </Typography>
                    ) : (
                      <Typography 
                        variant="h6"
                        sx={{ 
                          color: 'white', 
                          fontWeight: 600,
                          textAlign: 'center',
                          fontSize: { xs: '1.3rem', sm: '1.5rem' }
                        }}
                      >
                        {unpaidCount} Unpaid {unpaidCount === 1 ? 'Invoice' : 'Invoices'}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          );
        })()}
        
        {/* Total Amount Card - Second with Pay Now Button */}
        <Grid item xs={12} md={6}>
          <StyledCard sx={{ 
            background: 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)',
            color: 'white',
            boxShadow: '0 8px 24px rgba(76, 175, 80, 0.3)',
            height: '100%'
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <MoneyIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
                  Total Amount
                </Typography>
              </Box>
              <Typography variant={isMobile ? "h4" : "h3"} fontWeight={700} sx={{ mb: 2 }}>
                {formatCurrency(grandTotal)}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                size="medium"
                startIcon={<PaymentIcon />}
                onClick={handlePaySelected}
                disabled={selectedInvoices.size === 0}
                sx={{
                  backgroundColor: 'white',
                  color: '#4CAF50',
                  fontWeight: 700,
                  fontSize: { xs: '0.875rem', sm: '0.95rem' },
                  py: { xs: 1, sm: 1.2 },
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  mt: 'auto',
                  '&:hover': {
                    backgroundColor: '#f1f8f4',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'rgba(255, 255, 255, 0.7)',
                  }
                }}
              >
                {selectedInvoices.size === 0 
                  ? 'Select Invoices to Pay' 
                  : `Pay ${formatCurrency(selectedTotal)} (${selectedInvoices.size})`
                }
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Selection Controls */}
      <Box sx={{ 
        mb: 3, 
        p: 2, 
        background: 'white',
        borderRadius: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={selectedInvoices.size > 0 && selectedInvoices.size === invoiceData.filter(i => i.status?.toLowerCase() !== 'paid').length}
            indeterminate={selectedInvoices.size > 0 && selectedInvoices.size < invoiceData.filter(i => i.status?.toLowerCase() !== 'paid').length}
            onChange={handleToggleAll}
            sx={{
              color: '#FF7700',
              '&.Mui-checked': {
                color: '#FF7700',
              },
              '&.MuiCheckbox-indeterminate': {
                color: '#FF7700',
              }
            }}
          />
          <Typography variant="body1" fontWeight={600}>
            Select All Unpaid ({selectedInvoices.size} selected)
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Selected Total: <strong style={{ color: '#FF7700' }}>{formatCurrency(selectedTotal)}</strong>
        </Typography>
      </Box>

      {/* Invoices by Month */}
      {Object.entries(groupedInvoices).map(([month, invoices]) => {
        const monthSelectedInvoices = invoices.filter(inv => selectedInvoices.has(inv.id));
        const monthSelectedTotal = monthSelectedInvoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);
        
        return (
          <StyledCard key={month} sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                gap: { xs: 2, sm: 0 },
                mb: 3 
              }}>
                <Box>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#1e293b', mb: 0.5 }}>
                    {dayjs(month).format('MMMM YYYY')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {invoices.length} invoice{invoices.length > 1 ? 's' : ''}
                  </Typography>
                </Box>
                
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleViewReservations(month)}
                  sx={{
                    borderColor: '#FF7700',
                    color: '#FF7700',
                    borderRadius: 3,
                    fontWeight: 600,
                    '&:hover': {
                      background: 'rgba(255, 119, 0, 0.1)',
                      borderColor: '#E56900',
                    }
                  }}
                >
                  View Reservations
                </Button>
              </Box>
              
              {isMobile ? (
                // Mobile view - Cards
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {invoices.map((invoice) => {
                    const isPaid = invoice.status?.toLowerCase() === 'paid';
                    return (
                      <InvoiceCard key={invoice.id} status={invoice.status}>
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Checkbox
                                checked={selectedInvoices.has(invoice.id)}
                                onChange={() => handleToggleInvoice(invoice.id, isPaid)}
                                disabled={isPaid}
                                sx={{
                                  color: '#FF7700',
                                  '&.Mui-checked': {
                                    color: '#FF7700',
                                  }
                                }}
                              />
                              <Typography variant="h6" fontWeight={600} color="#2E4057">
                                {invoice.invoice_type}
                              </Typography>
                            </Box>
                            <Chip
                              icon={getStatusIcon(invoice.status)}
                              label={invoice.status}
                              color={getStatusColor(invoice.status)}
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                          
                          <Box sx={{ mb: 2, p: 2, background: 'rgba(255, 119, 0, 0.05)', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                            <Typography variant="h6" fontWeight={600} color="#FF7700">
                              {formatCurrency(invoice.total_amount)}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Due: {dayjs(invoice.due_date).format('DD/MM/YYYY')}
                            </Typography>
                            {invoice.pdf_file && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadPDF(invoice.pdf_file, invoice.invoice_type, invoice.id)}
                                sx={{
                                  borderColor: '#FF7700',
                                  color: '#FF7700',
                                  borderRadius: 2,
                                  '&:hover': {
                                    background: 'rgba(255, 119, 0, 0.1)',
                                  }
                                }}
                              >
                                PDF
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </InvoiceCard>
                    );
                  })}
                </Box>
              ) : (
                // Desktop view - Table
                <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: 'linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={invoices.every(inv => selectedInvoices.has(inv.id) || inv.status?.toLowerCase() === 'paid')}
                            indeterminate={
                              invoices.some(inv => selectedInvoices.has(inv.id) && inv.status?.toLowerCase() !== 'paid') &&
                              !invoices.every(inv => selectedInvoices.has(inv.id) || inv.status?.toLowerCase() === 'paid')
                            }
                            onChange={() => {
                              const unpaidInvoices = invoices.filter(inv => inv.status?.toLowerCase() !== 'paid');
                              const allSelected = unpaidInvoices.every(inv => selectedInvoices.has(inv.id));
                              
                              setSelectedInvoices(prev => {
                                const newSet = new Set(prev);
                                unpaidInvoices.forEach(inv => {
                                  if (allSelected) {
                                    newSet.delete(inv.id);
                                  } else {
                                    newSet.add(inv.id);
                                  }
                                });
                                return newSet;
                              });
                            }}
                            sx={{
                              color: '#FF7700',
                              '&.Mui-checked': {
                                color: '#FF7700',
                              },
                              '&.MuiCheckbox-indeterminate': {
                                color: '#FF7700',
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Invoice Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Due Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((invoice) => {
                        const isPaid = invoice.status?.toLowerCase() === 'paid';
                        return (
                          <TableRow 
                            key={invoice.id} 
                            hover
                            sx={{
                              backgroundColor: selectedInvoices.has(invoice.id) ? 'rgba(255, 119, 0, 0.03)' : 'inherit',
                              '&:hover': {
                                backgroundColor: selectedInvoices.has(invoice.id) ? 'rgba(255, 119, 0, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                              }
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedInvoices.has(invoice.id)}
                                onChange={() => handleToggleInvoice(invoice.id, isPaid)}
                                disabled={isPaid}
                                sx={{
                                  color: '#FF7700',
                                  '&.Mui-checked': {
                                    color: '#FF7700',
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} color="#2E4057">
                                {invoice.invoice_type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" fontWeight={700} color="#FF7700">
                                {formatCurrency(invoice.total_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getStatusIcon(invoice.status)}
                                label={invoice.status}
                                color={getStatusColor(invoice.status)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {dayjs(invoice.due_date).format('DD MMM YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {invoice.pdf_file && (
                                <Tooltip title="Download PDF">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadPDF(invoice.pdf_file, invoice.invoice_type, invoice.id)}
                                    sx={{
                                      color: '#FF7700',
                                      '&:hover': {
                                        background: 'rgba(255, 119, 0, 0.1)',
                                      }
                                    }}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </StyledCard>
        );
      })}

      {/* Reservations Dialog */}
      <Dialog
        open={reservationsDialog.open}
        onClose={() => setReservationsDialog({ open: false, month: '', reservations: [], loading: false })}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(90deg, #FF7700 0%, #FFA040 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarIcon />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Reservations for {dayjs(reservationsDialog.month).format('MMMM YYYY')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {reservationsDialog.reservations.length} reservation{reservationsDialog.reservations.length !== 1 ? 's' : ''} found
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, mt: 2 }}>
          {reservationsDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FF7700' }} />
            </Box>
          ) : reservationsDialog.reservations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CalendarIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No reservations found for this month
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Booking ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Guest Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Check In</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Check Out</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Original Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Final Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Commission</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reservationsDialog.reservations.map((reservation, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          #{reservation.booking_id || reservation.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {reservation.guest_name || reservation.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(reservation.check_in_date || reservation.checkin).format('DD/MM/YYYY')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(reservation.check_out_date || reservation.checkout).format('DD/MM/YYYY')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} color="text.secondary">
                          {formatCurrency(reservation.original_amount || reservation.amount || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="#2E4057">
                          {formatCurrency(reservation.final_amount || reservation.amount || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="#FF7700">
                          {formatCurrency(reservation.tabtrips_commission || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={reservation.booking_status || reservation.status || 'N/A'}
                          color={getStatusColor(reservation.booking_status || reservation.status)}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        {reservation.hotelier_voucher && reservation.hotelier_voucher.trim() && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => window.open(`https://api.tabtrips.com/olivrweb/hotelapi/${reservation.hotelier_voucher}`, '_blank')}
                            startIcon={<ReceiptIcon sx={{ fontSize: '0.8rem' }} />}
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.7rem',
                              py: 0.25,
                              px: 1,
                              borderRadius: '6px',
                              borderColor: '#FF7700',
                              color: '#FF7700',
                              '&:hover': {
                                borderColor: '#E56900',
                                backgroundColor: 'rgba(255,119,0,0.05)',
                              }
                            }}
                          >
                            Voucher
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button 
            onClick={() => setReservationsDialog({ open: false, month: '', reservations: [], loading: false })}
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: '#64748b',
              color: '#64748b'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayInvoiceDetails;

